import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import { capitalize } from 'lodash';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { proposalRouteConfig } from 'src/app/graph/routes';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ProposalStatus } from 'src/lib/graphqlTypes/types';

const validationSchema = Yup.object().shape({
  status: Yup.mixed<ProposalStatus>()
    .oneOf(Object.values(ProposalStatus))
    .required(),
});

type FormValues = Yup.InferType<typeof validationSchema>;

const getSelectStatusName = (status: ProposalStatus) => {
  switch (status) {
    case ProposalStatus.OPEN:
      return 'Open for feedback';
    case ProposalStatus.APPROVED:
    case ProposalStatus.DRAFT:
    case ProposalStatus.CLOSED:
    case ProposalStatus.IMPLEMENTED:
      return capitalize(status);
    default:
      assertUnreachableOrReturnDefault(status, capitalize(status));
  }
};

export const ChangeProposalStatusModal = ({
  proposalId,
  isOpen,
  onClose,
  currentStatus,
}: {
  proposalId: string;
  isOpen: boolean;
  onClose: () => void;
  currentStatus: ProposalStatus;
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const { graphId, graphVariant } = useRouteParams(proposalRouteConfig);
  const [changeStatus] = useMutation<
    GraphQLTypes.ChangeProposalStatusMutation,
    GraphQLTypes.ChangeProposalStatusMutationVariables
  >(gql`
    mutation ChangeProposalStatusMutation(
      $graphId: ID!
      $graphVariant: String!
      $status: ProposalStatus!
    ) {
      graph(id: $graphId) {
        variant(name: $graphVariant) {
          proposal {
            ... on ProposalMutation {
              updateStatus(status: $status) {
                ... on ValidationError {
                  message
                }
                ... on Proposal {
                  id
                  status
                }
              }
            }
          }
        }
      }
    }
  `);

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    isValid,
  } = useFormik<FormValues>({
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    initialValues: {
      status: currentStatus,
    },
    onSubmit: async ({ status }) => {
      const { data: responseData } = await changeStatus({
        variables: {
          graphId,
          graphVariant,
          status,
        },
        update: (cache) => {
          cache.evict({
            id: cache.identify({
              __typename: 'Proposal',
              id: proposalId,
            }),
            fieldName: 'status',
          });
        },
      });

      const responseProposal = responseData?.graph?.variant?.proposal;
      if (
        responseProposal?.__typename === 'ProposalMutation' &&
        responseProposal?.updateStatus.__typename === 'Proposal'
      ) {
        onClose();
        resetForm({});
        setErrorMessage('');
      } else if (
        responseProposal?.__typename === 'NotFoundError' ||
        responseProposal?.__typename === 'ValidationError' ||
        responseProposal?.__typename === 'PermissionError' ||
        responseProposal?.updateStatus.__typename === 'PermissionError' ||
        responseProposal?.updateStatus.__typename === 'ValidationError' ||
        !responseProposal?.updateStatus
      ) {
        setErrorMessage('An error has occurred');
      } else if (responseProposal.updateStatus.__typename !== 'Proposal') {
        setErrorMessage(
          assertUnreachableOrReturnDefault(
            responseProposal.updateStatus,
            'An error has occurred',
          ),
        );
      }
    },
    validationSchema,
  });

  const onCloseModal = useCallback(() => {
    onClose();
    resetForm({});
    setErrorMessage('');
  }, [onClose, resetForm]);

  return (
    <Modal size="lg" isOpen={isOpen} onClose={onCloseModal}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Change the status of your proposal</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <FormControl isInvalid={!!errors.status && !!touched.status}>
            <FormLabel>Status</FormLabel>
            <Select
              name="status"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.status}
              placeholder="Select a Status"
            >
              {Object.values(ProposalStatus)
                // Approved status is controlled by the backend so we
                // should filter it from this list
                .filter(
                  (statusName) =>
                    statusName !== ProposalStatus.APPROVED &&
                    statusName !== ProposalStatus.IMPLEMENTED,
                )
                .map((statusName) => (
                  <option value={statusName} key={statusName}>
                    {getSelectStatusName(statusName)}
                  </option>
                ))}
            </Select>
            <FormErrorMessage>{errors.status}</FormErrorMessage>
            {values.status === ProposalStatus.CLOSED && (
              <FormHelperText iconType="info">
                Proposals can be reopened at any time. Reviewers will be
                notified when proposals are closed or reopened.
              </FormHelperText>
            )}
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={onCloseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={touched.status && !isValid}
              isLoading={isSubmitting}
              loadingText="Editing"
              type="submit"
              onClick={() => handleSubmit()}
            >
              Edit Status
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
